import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { actionKeys, parseAction, type Action } from './entity';

/**
 * Action read queries.
 *
 * Bulletproof-style: each read has an exported `getXQueryOptions` factory
 * that returns a plain `queryOptions(...)` config, plus a thin hook that
 * spreads it into `useSuspenseQuery` and accepts caller-side overrides
 * via `queryConfig`.
 *
 * Callers can:
 *   - Use the hook for the standard case: `useAction({ id })`
 *   - Override per call site: `useQuery({ ...getActionQueryOptions(...), staleTime: Infinity })`
 *   - Prefetch from a route loader: `qc.prefetchQuery(getActionQueryOptions(...))`
 *   - Compose with other queries: `useQueries({ queries: [opts1, opts2] })`
 */

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* getAction — by-id with sub-action sideload                                 */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getAction(workspaceId: number, actionId: string, qc: QueryClient): Promise<Action> {
  const data = await hydraUiApi.actions.get({
    params: {
      path: { environment_id: String(workspaceId), action_id: actionId },
    },
  });
  // Sub-actions (split-test variants) sideload via `_actions`.
  // Populate their detail caches so a follow-up `useAction(variantId)`
  // resolves from cache.
  for (const sub of data._actions ?? []) {
    qc.setQueryData(actionKeys.detail(workspaceId, sub.id), parseAction(sub));
  }
  if (data.action == null) {
    throw new Error('missing action in get action response');
  }
  return parseAction(data.action);
}

export const getActionQueryOptions = (workspaceId: number, actionId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: actionKeys.detail(workspaceId, actionId),
    queryFn: () => getAction(workspaceId, actionId, qc),
    staleTime: STALE_TIME,
  });

interface UseActionOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getActionQueryOptions>;
}

export const useAction = ({ id, queryConfig }: UseActionOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getActionQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

// `useCampaignForAction` (the action → campaign FK follow) lives in
// `campaign/queries.ts` to avoid a circular import between the entity
// modules. The hook returns a `Campaign`, so co-locating it with the
// campaign reads is honest about where the data comes from.
